import { useState, useRef, useEffect } from "react";
import { createPortal } from 'react-dom';

/* CSS */
import styles from "./PriceSplitCell.module.css";
import shared from "../../styles/shared.module.css";

/* Icons */
import chevron_down from '../../assets/icons/chevron_down.svg';
import tick_icon_white from '../../assets/icons/tick_icon_white.svg';
import default_avatar from '../../assets/icons/default_avatar.svg';

/* Utils */
import { formatCurrency } from "../../utils/currencyUtils";
import { getMemberShare } from "../../utils/costUtils";

/* Normalizes the raw value to prevent null / undefined errors */
const normalizeValue = (value) => {
    if (value === null || value === undefined) {
        return { amount: 0, paidBy: null, splitWith: [], splitOption: 'equal', customSplits: {} }
    } else {
        // Ensure the object has the expected structure, even if backend sends partial data
        return {
            amount: value.amount ?? 0,
            paidBy: value.paidBy ? value.paidBy : null,
            splitWith: value.splitWith ? value.splitWith : [],
            splitOption: value.splitOption ? value.splitOption : 'equal',
            customSplits: value.customSplits ? value.customSplits : {}
        };
    }
};

const SPLIT_OPTIONS = {
    "equal": "Split equally",
    "exact": "Split by exact amounts",
    "percentage": "Split by percentages"
};

export const PriceSplitCell = ({ value, onChange, onContextMenu, availableUsers = [], isGuideMode, linkedPrice = null, onLinkedPriceChange }) => {

    // Normalized value
    const safe = normalizeValue(value);

    // Whether a Price column exists on this row (drives both the display and two-way sync)
    const hasLinkedPrice = linkedPrice != null;

    // Participants + each person's share, for the collapsed cell's stacked avatars
    const effectiveAmount = hasLinkedPrice ? linkedPrice : safe.amount;
    const participants = safe.splitWith
        .map(uId => availableUsers.find(u => u.id === uId))
        .filter(Boolean);
    const getUserShare = (userId) => getMemberShare(safe, userId, effectiveAmount);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Dropdown/Popover states
    const [isPaidByOpen, setIsPaidByOpen] = useState(false);
    const [isSplitWithOpen, setIsSplitWithOpen] = useState(false);
    const [isSplitOptionOpen, setIsSplitOptionOpen] = useState(false);

    // Tab states
    const [activeTab, setActiveTab] = useState('equal');
    const [localSplits, setLocalSplits] = useState({});

    // Working copy of 'safe' that the user edits inside the modal (so changes don't affect the real value until they hit Save)
    const [draft, setDraft] = useState(null);

    // Text shown in the price input (kept separate so it can show "0.00" by default but no leading zero while typing)
    const [amountStr, setAmountStr] = useState('0.00');

    // Error messages for split options
    const [validationError, setValidationError] = useState('');

    // Refs for click-outside detection on the two inline dropdowns
    const paidByRef = useRef(null);
    const splitWithRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isPaidByOpen && paidByRef.current && !paidByRef.current.contains(e.target)) {
                setIsPaidByOpen(false);
            }
            if (isSplitWithOpen && splitWithRef.current && !splitWithRef.current.contains(e.target)) {
                setIsSplitWithOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true); // capture phase — fires before stopPropagation can block it
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [isPaidByOpen, isSplitWithOpen]);

    // Get details
    const paidByUser = availableUsers.find(u => u.id === draft?.paidBy);
    const selectedUsers = (draft?.splitWith ?? []).map(uId =>
        availableUsers.find(u => u.id === uId)
    ).filter(Boolean);

    // Calculate total from localSplits
    const localSplitsTotal = Object.values(localSplits).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);

    // Display labels for the trigger pills
    const paidByLabel = paidByUser
        ? (paidByUser.isMe ? "You" : paidByUser.name)
        : "Select";

    const splitWithLabel = (() => {
        if (selectedUsers.length === 0) return "Select";
        if (selectedUsers.length === 1) {
            return selectedUsers[0].isMe ? "You" : selectedUsers[0].name;
        }
        const others = selectedUsers.length - 1;
        const othersLabel = `${others} other${others === 1 ? '' : 's'}`;
        return selectedUsers.some(u => u.isMe)
            ? `You and ${othersLabel}`
            : `${selectedUsers.length} people`;
    })();

    // Opens the main modal with a fresh draft snapshot
    const handleOpenModal = () => {
        const fresh = normalizeValue(value);
        // If a Price column is linked, it overrides the stored amount as the source of truth
        if (linkedPrice != null) {
            fresh.amount = linkedPrice;
        }
        setDraft(fresh);
        setAmountStr((fresh.amount || 0).toFixed(2));
        setIsModalOpen(true);
        setIsSplitOptionOpen(false);
    };

    // Adds or removes a user from the split list
    const handleToggleSplitWith = (userId) => {
        const isSelected = draft.splitWith.includes(userId);
        const newSplitWith = isSelected
            ? draft.splitWith.filter(id => id !== userId)
            : [...draft.splitWith, userId];
        setDraft(prev => ({ ...prev, splitWith: newSplitWith }));
    };

    // Dropdown list for the "Split with" multi-select
    const splitWithDropdown = (
        <div className={styles.dropdownMenu}>
            {availableUsers.map(user => {
                const isSelected = selectedUsers.some(u => u.id === user.id);
                return (
                    <div
                        key={user.id}
                        className={styles.dropdownItem}
                        onClick={() => handleToggleSplitWith(user.id)}
                    >
                        {user.avatar
                            ? <img src={user.avatar} alt={user.name} className={styles.dropdownAvatar} />
                            : <img src={default_avatar} alt={user.name} className={styles.dropdownAvatar} />
                        }
                        <span className={styles.dropdownName}>{user.name}</span>
                        <div className={`${styles.dropdownCheck} ${isSelected ? styles.dropdownCheckActive : ''}`}>
                            {isSelected && <img src={tick_icon_white} alt="Checked" />}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <>
            <div
                className={styles.priceSplitCellWrapper}
                onClick={handleOpenModal}
                onContextMenu={onContextMenu}
            >
                {isGuideMode ? (
                    <span className={styles.priceSplitPlaceholder}>
                        Select
                        <img src={chevron_down} alt="Chevron down" className={styles.priceSplitCellChevronDown} />
                    </span>
                ) : (
                    <div className={styles.priceSplitDisplay}>
                        <div className={styles.priceSplitDescriptor}>
                            {/* Hide the amount when a Price column already shows it; show it only when this cell is the sole place to see the price */}
                            {hasLinkedPrice
                                ? SPLIT_OPTIONS[safe.splitOption]
                                : <>{formatCurrency(safe.amount)} &nbsp;•&nbsp; {SPLIT_OPTIONS[safe.splitOption]}</>
                            }
                        </div>

                        {/* Stacked avatars of participants; CSS tooltip shows each person's share */}
                        {participants.length > 0 && (
                            <div className={styles.priceSplitAvatarsStack}>
                                {participants.map((user) => (
                                    <div
                                        key={user.id}
                                        className={styles.priceSplitAvatarWrapper}
                                    >
                                        <img
                                            src={user.avatar || default_avatar}
                                            alt={user.name}
                                            className={styles.priceSplitAvatarDisplay}
                                        />
                                        <span className={styles.priceSplitTooltip}>
                                            <strong>{user.isMe ? 'You' : user.name}</strong>: {formatCurrency(getUserShare(user.id))}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Main split settings modal */}
            {isModalOpen && createPortal(
                <div
                    className={shared.modalOverlay}
                    onPointerDown={(e) => e.stopPropagation()} // Stop dnd-kit sensors (portal events bubble through the React tree)
                    onMouseDown={(e) => e.stopPropagation()}   // Stop dnd-kit sensors
                >
                    <div
                        className={`${shared.modalContent} ${styles.settingsModal}`}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Title */}
                        <h2 className={styles.modalTitle}>Choose split settings</h2>

                        {/* Price (editable; synced back to the linked Price column on Save) */}
                        <div className={styles.priceWrapper}>
                            <span className={styles.priceSign}>$</span>
                            <input
                                className={styles.priceInput}
                                type="text"
                                inputMode="decimal"
                                style={{ width: `${Math.max(amountStr.length, 4)}ch` }}
                                value={amountStr}
                                onFocus={() => { if (parseFloat(amountStr) === 0) setAmountStr(''); }}
                                onBlur={() => setAmountStr((draft.amount || 0).toFixed(2))}
                                onChange={e => {
                                    const raw = e.target.value;
                                    if (raw === '' || /^\d*\.?\d{0,2}$/.test(raw)) {
                                        setAmountStr(raw);
                                        setDraft(prev => ({ ...prev, amount: parseFloat(raw) || 0 }));
                                    }
                                }}
                            />
                        </div>

                        {/* Options */}
                        <div className={styles.optionList}>
                            {/* Paid by */}
                            <div className={styles.optionRow}>
                                <div className={styles.optionLabel}>Paid by</div>
                                <div className={styles.optionControl} ref={paidByRef}>
                                    <div className={styles.optionTrigger} onClick={() => setIsPaidByOpen(prev => !prev)}>
                                        <span className={styles.optionPill}>{paidByLabel}</span>
                                        <img src={chevron_down} alt="Chevron down" className={styles.optionChevron} />
                                    </div>

                                    {isPaidByOpen && (
                                        <div className={styles.dropdownMenu}>
                                            {availableUsers.map(user => (
                                                <div
                                                    key={user.id}
                                                    className={`${styles.dropdownItem} ${user.id === draft.paidBy ? styles.dropdownItemSelected : ''}`}
                                                    onClick={() => {
                                                        setDraft(prev => ({
                                                            ...prev,
                                                            paidBy: user.id,
                                                            splitWith: prev.splitWith.includes(user.id) ? prev.splitWith : [...prev.splitWith, user.id] // Include the paidBy user in the split with
                                                        }));
                                                        setIsPaidByOpen(false);
                                                    }}
                                                >
                                                    {user.avatar
                                                        ? <img src={user.avatar} alt={user.name} className={styles.dropdownAvatar} />
                                                        : <img src={default_avatar} alt={user.name} className={styles.dropdownAvatar} />
                                                    }
                                                    <span className={styles.dropdownName}>{user.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Split with */}
                            <div className={styles.optionRow}>
                                <div className={styles.optionLabel}>Split with</div>
                                <div className={styles.optionControl} ref={splitWithRef}>
                                    <div className={styles.optionTrigger} onClick={() => setIsSplitWithOpen(prev => !prev)}>
                                        <span className={styles.optionPill}>{splitWithLabel}</span>
                                        <img src={chevron_down} alt="Chevron down" className={styles.optionChevron} />
                                    </div>

                                    {isSplitWithOpen && splitWithDropdown}
                                </div>
                            </div>

                            {/* Split option */}
                            <div className={styles.optionRow}>
                                <div className={styles.optionLabel}>Split option</div>
                                <div className={styles.optionControl}>
                                    <div
                                        className={styles.optionTrigger}
                                        onClick={() => {
                                            setActiveTab(draft.splitOption);
                                            setLocalSplits({ ...draft.customSplits });
                                            setIsSplitOptionOpen(true);
                                            setValidationError('');
                                        }}
                                    >
                                        <span className={styles.optionPill}>{SPLIT_OPTIONS[draft.splitOption]}</span>
                                        <img src={chevron_down} alt="Chevron down" className={styles.optionChevron} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className={styles.modalActions}>
                            <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className={styles.saveBtn}
                                onClick={() => {
                                    onChange(draft);
                                    // Two-way sync: push the amount back to the linked Price column
                                    if (hasLinkedPrice && draft.amount !== linkedPrice) {
                                        onLinkedPriceChange?.(draft.amount);
                                    }
                                    setIsModalOpen(false);
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Split option modal */}
            {isSplitOptionOpen && createPortal(
                <div
                    className={shared.modalOverlay}
                    onPointerDown={(e) => e.stopPropagation()} // Stop dnd-kit sensors (portal events bubble through the React tree)
                    onMouseDown={(e) => e.stopPropagation()}   // Stop dnd-kit sensors
                >
                    <div className={shared.modalContent} onClick={e => e.stopPropagation()}>
                        {/* Error */}
                        {validationError && <p className={styles.errorMessage}>{validationError}</p>}

                        {/* Tabs */}
                        <div className={styles.tabs}>
                            <button onClick={() => { setActiveTab("equal"); setValidationError('') }} className={`${styles.tab} ${activeTab === 'equal' ? styles.activeTab : ''}`}><span className={styles.tabIcon}>=</span></button>
                            <button onClick={() => { setActiveTab("exact"); setValidationError('') }} className={`${styles.tab} ${activeTab === 'exact' ? styles.activeTab : ''}`}><span className={`${styles.tabIcon} ${styles.exactAmount}`}>1.23</span></button>
                            <button onClick={() => { setActiveTab("percentage"); setValidationError('') }} className={`${styles.tab} ${activeTab === 'percentage' ? styles.activeTab : ''}`}><span className={styles.tabIcon}>%</span></button>
                        </div>

                        {/* Title */}
                        <h2 className={styles.splitOptionTitle}>{SPLIT_OPTIONS[activeTab]}</h2>

                        {/* Input */}
                        <div>
                            {activeTab === "equal" && (
                                <div>
                                    {selectedUsers.length === 0 ? (
                                        <p className={styles.noMembersMessage}>
                                            Please select who's splitting this cost
                                        </p>
                                    ) : (
                                        selectedUsers.map(user => (
                                            <div key={user.id} className={styles.splitOptionWrapper}>
                                                <div className={styles.splitOptionRow}>
                                                    <div className={styles.splitOptionMember}>
                                                        {user.avatar
                                                            ? <img src={user.avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                            : <img src={default_avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                        }
                                                        <label className={styles.splitOptionName}>{user.isMe ? "You" : user.name}</label>
                                                    </div>
                                                    <span className={styles.splitOptionAmount}>{formatCurrency(selectedUsers.length > 0 ? draft.amount / selectedUsers.length : 0)}</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                            {activeTab === "exact" && (
                                <div>
                                    {selectedUsers.length === 0 ? (
                                        <p className={styles.noMembersMessage}>
                                            Please select who's splitting this cost
                                        </p>
                                    ) : (
                                        selectedUsers.map(user => (
                                            <div key={user.id} className={styles.splitOptionWrapper}>
                                                <div className={styles.splitOptionRow}>
                                                    <div className={styles.splitOptionMember}>
                                                        {user.avatar
                                                            ? <img src={user.avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                            : <img src={default_avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                        }
                                                        <label className={styles.splitOptionName}>{user.isMe ? "You" : user.name}</label>
                                                    </div>
                                                    <div className={styles.splitOptionAmount}>
                                                        <span>$</span>
                                                        <input
                                                            className={styles.splitInput}
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={localSplits[user.id] ?? '0'}
                                                            onChange={e => {
                                                                const raw = e.target.value;
                                                                if (raw === '' || /^\d*\.?\d{0,2}$/.test(raw)) {
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: raw }));
                                                                }
                                                            }}
                                                            onFocus={() => {
                                                                if (!localSplits[user.id] || parseFloat(localSplits[user.id]) === 0)
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: '' }));
                                                            }}
                                                            onBlur={() => {
                                                                if (localSplits[user.id] === '')
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: 0 }));
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}

                                    {selectedUsers.length !== 0 ? (
                                        <div className={styles.splitSummary}>
                                            {/* Total */}
                                            <span className={styles.splitSummaryRow}>
                                                <span>Total</span>
                                                <span>{formatCurrency(localSplitsTotal)}</span>
                                            </span>

                                            {/* Remaining or Over */}
                                            <span className={`${styles.splitSummaryRow} ${(draft.amount - localSplitsTotal) < 0 ? styles.overAmount : styles.remainingAmount}`}>
                                                <span>{(draft.amount - localSplitsTotal) < 0 ? "Over by:" : "Remaining:"}</span>
                                                <span>{formatCurrency(Math.abs(draft.amount - localSplitsTotal))}</span>
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                            {activeTab === "percentage" && (
                                <div>
                                    {selectedUsers.length === 0 ? (
                                        <p className={styles.noMembersMessage}>
                                            Please select who's splitting this cost
                                        </p>
                                    ) : (
                                        selectedUsers.map(user => (
                                            <div key={user.id} className={styles.splitPercentageWrapper}>
                                                <div className={styles.splitOptionRow}>
                                                    <div className={styles.splitOptionMember}>
                                                        {user.avatar
                                                            ? <img src={user.avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                            : <img src={default_avatar} alt={user.name} className={styles.splitOptionAvatar} />
                                                        }
                                                        <label className={styles.splitOptionName}>{user.isMe ? "You" : user.name}</label>
                                                    </div>
                                                    <div className={styles.splitOptionAmount}>
                                                        <input
                                                            className={styles.splitInput}
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={localSplits[user.id] ?? '0'}
                                                            onChange={e => {
                                                                const raw = e.target.value;
                                                                if (raw === '' || /^\d*\.?\d{0,2}$/.test(raw)) {
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: raw }));
                                                                }
                                                            }}
                                                            onFocus={() => {
                                                                if (!localSplits[user.id] || parseFloat(localSplits[user.id]) === 0)
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: '' }));
                                                            }}
                                                            onBlur={() => {
                                                                if (localSplits[user.id] === '')
                                                                    setLocalSplits(prev => ({ ...prev, [user.id]: 0 }));
                                                            }}
                                                        />
                                                        <span>%</span>
                                                    </div>
                                                </div>
                                                <div className={styles.splitPercentageAmount}>
                                                    {formatCurrency((parseFloat(localSplits[user.id]) || 0) / 100 * draft.amount)}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className={styles.splitOptionActions}>
                            <button onClick={() => setIsSplitOptionOpen(false)} className={styles.splitOptionCancelBtn}>
                                Cancel
                            </button>
                            <button
                                className={styles.splitOptionSaveBtn}
                                onClick={() => {
                                    // Validate exact
                                    if (activeTab === 'exact' && Math.abs(localSplitsTotal - draft.amount) > 0.01) {
                                        setValidationError('The total amount must match the original amount');
                                        return;
                                    }
                                    // Validate percentage
                                    if (activeTab === 'percentage' && Math.abs(localSplitsTotal - 100) > 0.01) {
                                        setValidationError('The total percentage must add up to 100%');
                                        return;
                                    }
                                    setValidationError('');
                                    setDraft(prev => ({ ...prev, splitOption: activeTab, customSplits: activeTab === 'equal' ? {} : localSplits }));
                                    setIsSplitOptionOpen(false);
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
